import React, { useState } from 'react';
import { Typography, Button, Table, Tag, Switch, Space, Popconfirm, Empty } from 'antd';
import { PlusOutlined, BellOutlined } from '@ant-design/icons';
import { useListAlertsQuery, useCreateAlertMutation, useUpdateAlertMutation, useDeleteAlertMutation } from './alertsApi';
import AlertForm from './AlertForm';

const { Title, Text } = Typography;

const TYPE_COLORS = {
  keyword: 'geekblue',
  category: 'cyan',
  merchant: 'purple',
  price_threshold: 'orange',
  any: 'default',
};

export default function AlertsPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const { data: alerts = [], isLoading } = useListAlertsQuery();
  const [createAlert] = useCreateAlertMutation();
  const [updateAlert] = useUpdateAlertMutation();
  const [deleteAlert] = useDeleteAlertMutation();

  const handleCreate = async (values) => {
    await createAlert(values);
    setFormOpen(false);
  };

  const handleUpdate = async (values) => {
    await updateAlert({ publicId: editingAlert.publicId, ...values });
    setEditingAlert(null);
  };

  const handleToggle = (record, checked) => {
    updateAlert({ publicId: record.publicId, isActive: checked });
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={TYPE_COLORS[type] || 'default'} style={{ borderRadius: 6, fontWeight: 600 }}>
          {type?.replace(/_/g, ' ')}
        </Tag>
      ),
    },
    {
      title: 'Criteria',
      key: 'criteria',
      render: (_, r) => {
        if (r.keyword) return <Text style={{ color: 'var(--df-text)' }}>“{r.keyword}”</Text>;
        if (r.minDiscountPercent) return `${r.minDiscountPercent}%+ off`;
        if (r.maxPriceMinor) return `Under $${(r.maxPriceMinor / 100).toFixed(2)}`;
        return <Text type="secondary">Any deal</Text>;
      },
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      render: (f) => <Text style={{ color: 'var(--df-text-secondary)', fontSize: 13 }}>{f?.replace(/_/g, ' ')}</Text>,
    },
    {
      title: 'Active',
      key: 'active',
      render: (_, record) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => handleToggle(record, checked)}
          aria-label={`Toggle alert ${record.publicId}`}
        />
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d) => (d ? new Date(d).toLocaleDateString() : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => setEditingAlert(record)} style={{ borderRadius: 6, minHeight: 36 }}>
            Edit
          </Button>
          <Popconfirm title="Delete this alert?" onConfirm={() => deleteAlert(record.publicId)}>
            <Button size="small" danger style={{ borderRadius: 6, minHeight: 36 }}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <section className="df-page-hero" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <Title
              level={3}
              style={{ margin: 0, fontFamily: 'var(--df-font-display)', color: 'var(--df-text)' }}
            >
              <BellOutlined style={{ marginRight: 10, color: 'var(--df-accent)' }} />
              My Alerts
            </Title>
            <Text style={{ display: 'block', marginTop: 6, color: 'var(--df-text-secondary)' }}>
              Get notified when deals match your keywords, prices, or discount thresholds.
            </Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setFormOpen(true)}
            style={{
              borderRadius: 8,
              fontWeight: 600,
              height: 44,
              background: 'var(--df-accent)',
              borderColor: 'var(--df-accent)',
            }}
          >
            Create Alert
          </Button>
        </div>
      </section>

      {alerts.length === 0 && !isLoading ? (
        <div
          style={{
            padding: '64px 24px',
            textAlign: 'center',
            borderRadius: 12,
            border: '1px dashed var(--df-border)',
            background: 'rgba(255,255,255,0.55)',
          }}
        >
          <Empty description="No alerts yet — create one to catch deals as they land" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      ) : (
        <div
          style={{
            borderRadius: 12,
            border: '1px solid var(--df-border)',
            background: 'var(--df-surface-elevated)',
            boxShadow: 'var(--df-shadow)',
            overflow: 'hidden',
          }}
        >
          <Table
            dataSource={alerts}
            columns={columns}
            rowKey="publicId"
            loading={isLoading}
            pagination={false}
            scroll={{ x: 720 }}
          />
        </div>
      )}

      <AlertForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreate}
      />

      {editingAlert && (
        <AlertForm
          open={!!editingAlert}
          onClose={() => setEditingAlert(null)}
          onSubmit={handleUpdate}
          initialValues={editingAlert}
        />
      )}
    </div>
  );
}
